/**
 * NbyulaCliSection — Advanced-tab card for the embedded `nbyula` Discovery CLI.
 *
 * Modeled on the Claude Code CLI section in SettingsPage: same resolution model
 * (active path / source / system / bundled detection + an optional path
 * override), extended with the version, check-for-updates, update, and
 * update/revert controls this CLI's pinned model needs. Backed by
 * `GET/PUT /api/settings/nbyula-cli` and `POST .../{check,update}`.
 *
 * Local component state, refetched after each action (no store, no websocket) —
 * matching the Claude CLI / Browser Engine sections.
 */

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/common/Button";
import { apiFetch } from "@/api/client";

interface NbyulaCliInfo {
  // claude-cli-shaped resolution fields
  active_path: string | null;
  source: "configured" | "writable" | "embedded" | "system" | "none";
  configured_path: string | null;
  system_path: string | null;
  bundled_path: string | null;
  bundled_available: boolean;
  // pinned-model extras
  pinned_spec: string | null;
  writable_path: string | null;
  current_version: string | null;
  managed_version: string | null;
  bundled_version: string | null;
  latest_known: string | null;
  update_available: boolean;
  can_revert: boolean;
  target: string | null;
  // Launch-time auto-update opt-in (default on). Undefined on older backends —
  // treated as on so the toggle reflects the actual default.
  auto_update?: boolean;
}

/** Extra fields the live "Check" (peek_manifest) returns on top of CliInfo. */
interface CheckResult {
  ok: boolean;
  latest_version: string | null;
  absolute_latest: string | null;
  newer_band_available: boolean;
  error: string | null;
}

/** Live download/install progress for the manual "Update" flow (polled). */
interface UpdateProgress {
  status: "idle" | "starting" | "downloading" | "done" | "error";
  downloaded: number;
  total: number;
  pct: number;
  from_version: string | null;
  to_version: string | null;
  updated: boolean;
  error: string | null;
}

/**
 * Whether the managed `nbyula` is on the *user's* terminal PATH (separate from
 * the agent's PATH). On POSIX it's a symlink into a PATH dir; on Windows a
 * registry PATH entry for the managed bin dir. Backed by
 * `GET/POST/DELETE /api/settings/nbyula-cli/system-link`.
 */
interface SystemLinkStatus {
  supported: boolean;
  platform: string;
  method: "symlink" | "path";
  managed_available: boolean;
  linked: boolean;
  link_path: string | null;
  link_target: string | null;
  on_path: boolean;
  version: string | null;
  candidates: string[];
  conflict: { path: string; version: string | null } | null;
  shell_rc: string | null;
}

/** The action outcome returned alongside refreshed status by POST/DELETE. */
interface SystemLinkResult {
  ok: boolean;
  message: string;
  error: string | null;
  path_updated?: boolean;
  shell_rc?: string | null;
  removed?: string[];
}

const SOURCE_LABEL: Record<NbyulaCliInfo["source"], string> = {
  configured: "custom",
  writable: "managed",
  embedded: "bundled",
  system: "system",
  none: "none",
};

function formatBytes(n: number): string {
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export function NbyulaCliSection() {
  const [info, setInfo] = useState<NbyulaCliInfo | null>(null);
  const [pathInput, setPathInput] = useState("");
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [progress, setProgress] = useState<UpdateProgress | null>(null);
  const [savingPath, setSavingPath] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [link, setLink] = useState<SystemLinkStatus | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [linkMessage, setLinkMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  // setInterval handle for the update progress poll; cleared on terminal
  // status or unmount so a closed Settings panel never leaves it running.
  const pollRef = useRef<number | null>(null);

  const apply = (i: NbyulaCliInfo) => {
    setInfo(i);
    setPathInput(i.configured_path ?? "");
  };

  const load = () => {
    apiFetch<NbyulaCliInfo>("/settings/nbyula-cli").then(apply).catch(() => {});
  };

  const loadLink = () => {
    apiFetch<SystemLinkStatus>("/settings/nbyula-cli/system-link")
      .then(setLink)
      .catch(() => {});
  };

  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // Poll the shared progress snapshot (~every 350ms) and settle on done/error.
  // Used both by the Update button here AND when we resume an update that was
  // started elsewhere (launch screen / auto-update / another tab).
  const startPolling = () => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const p = await apiFetch<UpdateProgress>("/settings/nbyula-cli/update/progress");
        setProgress(p);
        if (p.status === "done" || p.status === "error") {
          stopPolling();
          setUpdating(false);
          if (p.status === "error") {
            setMessage({ ok: false, text: p.error || "Update failed" });
          } else {
            setMessage({
              ok: true,
              text: p.updated ? `Updated to v${p.to_version}` : "Already up to date",
            });
          }
          load(); // refresh resolution/version after the swap
        }
      } catch (e) {
        stopPolling();
        setUpdating(false);
        setMessage({ ok: false, text: (e as Error).message });
      }
    }, 350);
  };

  // If a CLI update is already running when this panel opens (started by the
  // launch screen, auto-update, or another Settings tab), reflect it right away:
  // show the bar, disable the buttons, and resume polling. The backend keeps a
  // single module-level progress snapshot, so this stays accurate and a second
  // Update press can't kick off a duplicate download.
  const resumeIfUpdating = async () => {
    try {
      const p = await apiFetch<UpdateProgress>("/settings/nbyula-cli/update/progress");
      if (p.status === "starting" || p.status === "downloading") {
        setUpdating(true);
        setProgress(p);
        startPolling();
      }
    } catch { /* ignore — nothing in flight */ }
  };

  useEffect(() => {
    load();
    loadLink();
    resumeIfUpdating();
    return stopPolling; // tidy up the poll if the panel unmounts mid-update
  }, []);

  const onCheck = async () => {
    setChecking(true);
    setMessage(null);
    try {
      const i = await apiFetch<NbyulaCliInfo & { check: CheckResult }>(
        "/settings/nbyula-cli/check",
        { method: "POST" },
      );
      apply(i);
      if (i.check?.error) setMessage({ ok: false, text: `Check failed: ${i.check.error}` });
      else if (i.update_available) setMessage({ ok: true, text: `Update available: v${i.latest_known}` });
      else if (i.check?.newer_band_available)
        // Up to date within the pin, but a newer minor/major exists outside it.
        setMessage({
          ok: true,
          text: `Up to date on ${i.pinned_spec}. v${i.check.absolute_latest} exists outside the pin — update CoWork to move it.`,
        });
      else setMessage({ ok: true, text: "Up to date" });
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setChecking(false);
    }
  };

  // The update is non-blocking server-side: POST starts it, then we poll the
  // progress snapshot (~every 350ms) to drive the live download bar, stopping
  // once it reports `done`/`error`. The atomic swap means a closed panel never
  // corrupts the binary, so it's safe to just stop polling on unmount.
  const onUpdate = async () => {
    setUpdating(true);
    setMessage(null);
    setProgress({
      status: "starting", downloaded: 0, total: 0, pct: 0,
      from_version: null, to_version: null, updated: false, error: null,
    });
    try {
      await apiFetch<UpdateProgress>("/settings/nbyula-cli/update", { method: "POST" });
    } catch (e) {
      setUpdating(false);
      setProgress(null);
      setMessage({ ok: false, text: (e as Error).message });
      return;
    }
    startPolling();
  };

  // Throw away an explicitly-updated CLI and pin back to the bundled version.
  const onRevert = async () => {
    setReverting(true);
    setMessage(null);
    try {
      const i = await apiFetch<NbyulaCliInfo & { revert: { ok: boolean; version: string | null; error: string | null } }>(
        "/settings/nbyula-cli/revert",
        { method: "POST" },
      );
      apply(i);
      if (i.revert?.ok) {
        setMessage({ ok: true, text: `Reverted to bundled v${i.revert.version ?? i.bundled_version}` });
      } else {
        setMessage({ ok: false, text: i.revert?.error || "Revert failed" });
      }
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setReverting(false);
    }
  };

  const savePath = async (path: string) => {
    setSavingPath(true);
    setMessage(null);
    try {
      apply(
        await apiFetch<NbyulaCliInfo>("/settings/nbyula-cli", {
          method: "PUT",
          body: JSON.stringify({ path }),
        }),
      );
      setMessage({ ok: true, text: path ? "Custom path saved." : "Override cleared." });
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setSavingPath(false);
    }
  };

  // Flip the launch-time auto-update opt-in. Optimistic (the switch should feel
  // instant), reconciled with the server's view, reverted if the write fails.
  // Uses setInfo (not apply) so it never resets pathInput — toggling auto-update
  // must not discard an unsaved edit in the custom-path field.
  const setAutoUpdate = async (enabled: boolean) => {
    setSavingAuto(true);
    setMessage(null);
    setInfo((prev) => (prev ? { ...prev, auto_update: enabled } : prev));
    try {
      setInfo(
        await apiFetch<NbyulaCliInfo>("/settings/nbyula-cli", {
          method: "PUT",
          body: JSON.stringify({ auto_update: enabled }),
        }),
      );
    } catch (e) {
      setInfo((prev) => (prev ? { ...prev, auto_update: !enabled } : prev));
      setMessage({ ok: false, text: (e as Error).message });
    } finally {
      setSavingAuto(false);
    }
  };

  // Install / remove the managed `nbyula` on the user's own terminal PATH. POST
  // returns refreshed status under the top level plus the action outcome under
  // `install`; DELETE mirrors it under `uninstall`.
  const onLink = async () => {
    setLinking(true);
    setLinkMessage(null);
    try {
      const r = await apiFetch<SystemLinkStatus & { install: SystemLinkResult }>(
        "/settings/nbyula-cli/system-link",
        { method: "POST", body: JSON.stringify({ modify_shell_rc: true }) },
      );
      setLink(r);
      setLinkMessage({ ok: r.install.ok, text: r.install.error || r.install.message });
    } catch (e) {
      setLinkMessage({ ok: false, text: (e as Error).message });
    } finally {
      setLinking(false);
    }
  };

  const onUnlink = async () => {
    setUnlinking(true);
    setLinkMessage(null);
    try {
      const r = await apiFetch<SystemLinkStatus & { uninstall: SystemLinkResult }>(
        "/settings/nbyula-cli/system-link",
        { method: "DELETE" },
      );
      setLink(r);
      setLinkMessage({ ok: r.uninstall.ok, text: r.uninstall.error || r.uninstall.message });
    } catch (e) {
      setLinkMessage({ ok: false, text: (e as Error).message });
    } finally {
      setUnlinking(false);
    }
  };

  if (!info) {
    return <p className="text-xs text-space-40">Loading...</p>;
  }

  return (
    <div className="rounded-xl border border-space-30 p-4 space-y-3">
      {/* Status indicator */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${
            !info.active_path
              ? "bg-red-400"
              : info.update_available
                ? "bg-amber-500"
                : "bg-emerald-500"
          }`}
        />
        <span className="text-sm text-space">
          {info.active_path
            ? `Using ${SOURCE_LABEL[info.source]} CLI`
            : "nbyula CLI not found"}
        </span>
        {info.current_version && (
          <span className="text-xs text-space-40">v{info.current_version}</span>
        )}
        {info.update_available && (
          <span className="ml-auto text-xs text-amber-600">
            ⚠ v{info.latest_known} available
          </span>
        )}
      </div>

      {/* Resolution details */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
        <InfoRow label="Active" value={info.active_path ?? "none"} mono />
        <InfoRow label="Source" value={SOURCE_LABEL[info.source]} />
        <InfoRow label="Managed" value={info.managed_version ? `v${info.managed_version}` : "—"} />
        <InfoRow label="Pinned" value={info.pinned_spec ?? "—"} />
        <InfoRow label="Platform" value={info.target ?? "unsupported"} />
        {info.system_path && <InfoRow label="System" value={info.system_path} mono />}
        <InfoRow
          label="Bundled"
          value={info.bundled_version ? `v${info.bundled_version}` : info.bundled_available ? "available" : "not bundled"}
        />
      </div>

      {/* Update controls */}
      <div className="border-t border-space-20 pt-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={checking}
            disabled={updating}
            onClick={onCheck}
          >
            Check for updates
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={updating}
            disabled={updating || !info.update_available}
            onClick={onUpdate}
          >
            {info.update_available ? `Update to v${info.latest_known}` : "Update"}
          </Button>
          {message && (
            <span
              className={`ml-auto text-xs flex items-center gap-1 ${
                message.ok ? "text-emerald-600" : "text-hydra"
              }`}
            >
              {message.ok ? (
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {message.text}
            </span>
          )}
        </div>

        {/* Auto-update on launch — within the pinned band (the pin's
            specificity is the ceiling; loosen it in cowork/cli_version.py to let
            auto-update pull minors/majors). Default on. */}
        <label className="flex items-start gap-2 text-xs text-space cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 h-3.5 w-3.5 rounded border-space-30 text-jove focus:ring-jove disabled:opacity-50"
            checked={info.auto_update !== false}
            disabled={savingAuto || updating}
            onChange={(e) => setAutoUpdate(e.target.checked)}
          />
          <span>
            Auto-update on launch
            <span className="text-space-40">
              {" "}— keep the CLI on the newest {info.pinned_spec ?? "pinned"} release; you’ll see a toast when it bumps
            </span>
          </span>
        </label>

        {/* Live download bar — mirrors install.sh's [####    ] NN% progress. */}
        {updating && progress && progress.status !== "done" && progress.status !== "error" && (
          <div className="space-y-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-space-20">
              <div
                className={`h-full rounded-full bg-jove transition-[width] duration-200 ease-out ${
                  progress.status === "starting" ? "animate-pulse" : ""
                }`}
                style={{ width: `${progress.status === "starting" ? 8 : progress.pct}%` }}
              />
            </div>
            <p className="text-xs text-space-40">
              {progress.status === "starting"
                ? "Preparing update…"
                : progress.total > 0
                  ? `Downloading ${formatBytes(progress.downloaded)} / ${formatBytes(progress.total)} — ${progress.pct}%`
                  : `Downloading ${formatBytes(progress.downloaded)}…`}
            </p>
          </div>
        )}
      </div>

      {/* Revert to the version CoWork bundled (undo an explicit update) */}
      <div className="flex items-center justify-between gap-3 border-t border-space-20 pt-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-space">
            Pinned to {info.pinned_spec ?? "the bundled CLI"}
          </p>
          <p className="text-xs text-space-40">
            {info.can_revert
              ? `On v${info.managed_version} — bundled is v${info.bundled_version}. Revert to undo the update.`
              : `Using the version CoWork shipped with${info.bundled_version ? ` (v${info.bundled_version})` : ""}. Update stays within ${info.pinned_spec ?? "the pin"}.`}
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          loading={reverting}
          disabled={!info.can_revert || updating}
          onClick={onRevert}
        >
          Revert to bundled version
        </Button>
      </div>

      {/* Run nbyula in your own terminal — symlink (POSIX) / PATH entry (Windows)
          pointing at the same managed binary the agent uses. */}
      {link && link.supported && (
        <div className="border-t border-space-20 pt-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium text-space">Use in your terminal</p>
              <p className="text-xs text-space-40">
                {link.linked
                  ? link.method === "symlink"
                    ? `Linked: ${link.link_path}${link.version ? ` (v${link.version})` : ""}`
                    : `On your PATH: ${link.link_path}`
                  : link.method === "symlink"
                    ? "Symlink nbyula into a PATH dir so you can run it from any terminal."
                    : "Add nbyula to your user PATH so you can run it from any terminal."}
              </p>
            </div>
            {link.linked ? (
              <Button variant="secondary" size="sm" loading={unlinking} disabled={linking} onClick={onUnlink}>
                Remove
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                loading={linking}
                disabled={unlinking || !link.managed_available}
                onClick={onLink}
              >
                Add to terminal
              </Button>
            )}
          </div>
          {link.linked && !link.on_path && (
            <p className="text-xs text-amber-600">
              ⚠ {link.link_path?.replace(/\/nbyula$/, "")} isn't on your PATH yet — open a new terminal
              {link.shell_rc ? ` (or source ${link.shell_rc})` : ""}.
            </p>
          )}
          {link.conflict && (
            <p className="text-xs text-amber-600">
              ⚠ Another nbyula is already on your PATH at {link.conflict.path}
              {link.conflict.version ? ` (v${link.conflict.version})` : ""} — it may shadow this one.
            </p>
          )}
          {linkMessage && (
            <p className={`text-xs ${linkMessage.ok ? "text-emerald-600" : "text-hydra"}`}>
              {linkMessage.text}
            </p>
          )}
        </div>
      )}

      {/* Manual path override (mirrors the Claude CLI section) */}
      <div className="border-t border-space-20 pt-3 space-y-2">
        <p className="text-xs font-medium text-space-40">
          Custom CLI Path <span className="font-normal">(optional override)</span>
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={pathInput}
            onChange={(e) => setPathInput(e.target.value)}
            placeholder="/usr/local/bin/nbyula"
            className="flex-1 rounded-lg border border-space-30 bg-space-10 px-3 py-1.5 text-xs font-mono text-space placeholder:text-space-30 focus:border-jove focus:outline-none"
          />
          <Button variant="secondary" size="sm" loading={savingPath} onClick={() => savePath(pathInput.trim())}>
            Save
          </Button>
          {pathInput && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPathInput("");
                savePath("");
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <p className="text-xs text-space-40">
          Leave empty to use the bundled CLI. Resolution order: custom path → managed (explicitly updated) → bundled floor → system install.
          A custom binary must be named <span className="font-mono">nbyula</span>; Update/Revert still manage the bundled copy within the {info.pinned_spec ?? "pin"} pin.
        </p>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-space-40 truncate">{label}</span>
      <span className={`text-xs text-space truncate text-right ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
